import React from 'react';
import { useAppDispatch } from '../../store';
import { showModal } from '../../store/actions/modalActions';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { TagItem } from './documents/types';
import { PiTagLight } from 'react-icons/pi';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

const MAX_VISIBLE_TAGS = 2;

interface TagPickerProps {
  tags: TagItem[];
  fileId?: string;
  folderId?: string;
  onTagsChanged?: (tags: TagItem[]) => void;
}

const TagPicker: React.FC<TagPickerProps> = ({
  tags,
  fileId,
  folderId,
  onTagsChanged,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const openTagModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(showModal({
      type: 'tagPicker',
      showModal: true,
      message: t('Manage tags'),
      subMsg: t('Select tags to assign or remove from this item.'),
      icon: <PiTagLight className='w-4 h-4' />,
      tags: tags,
      fileId: fileId,
      folderId: folderId,
      onTagsChanged: onTagsChanged,
      width: '600px',
    }));
  };

  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTags = tags.slice(MAX_VISIBLE_TAGS);

  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      {/* Manage tags button - always visible first */}
      <Tooltip title={t('Manage tags')} arrow placement="top">
        <button
          type="button"
          onClick={openTagModal}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-fg-muted hover:text-champagne hover:bg-surface-sunken/70 transition-colors flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
        >
          <PiTagLight className="w-3 h-3" />
        </button>
      </Tooltip>

      {visibleTags.map(tag => (
        <Badge
          key={tag.id}
          color={(tag.color as BadgeColor) || 'gray'}
          className="text-[10px]"
        >
          {tag.name}
        </Badge>
      ))}

      {hiddenTags.length > 0 && (
        <Tooltip
          title={
            <div className="flex flex-col gap-1 py-1">
              {hiddenTags.map(tag => (
                <div key={tag.id} className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: `var(--color-${tag.color || 'gray'}-500, #6b7280)` }}
                  />
                  <span>{tag.name}</span>
                </div>
              ))}
            </div>
          }
          arrow
          placement="bottom"
        >
          <button
            type="button"
            onClick={openTagModal}
            className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full border border-hairline-soft bg-surface-sunken/70 text-[10px] text-fg-body font-medium cursor-pointer hover:border-champagne/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
          >
            +{hiddenTags.length}
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default TagPicker;
